import { createContext, PropsWithChildren, useCallback, useContext, useEffect } from "react";
import { SvgIcon } from "@mui/material";
import { FormattedMessage, useIntl } from "react-intl";
import { cloneDeep, defaultsDeep, isEqual } from "lodash-es";

import SettingsRemoteOutlinedIcon from "@mui/icons-material/SettingsRemoteOutlined";

import { ReactComponent as PartitionsIcon } from "../../../../icons/format-list-bulleted-type.svg";
import { ReactComponent as BoardsIcon } from "../../../../icons/developer-board.svg";
import { ReactComponent as RfidCardsIconIcon } from "../../../../icons/card-rfid.svg";
import { ReactComponent as SmartalarmIcon } from "../../../../icons/smartalarm.svg";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import HourglassTopIcon from "@mui/icons-material/HourglassTop";

import { BeforeSaveFunction, BeforeSaveErrorType } from "../../BasicTemplate";
import { IOPortItem, Partition, RemotecontrolValue, SmartalarmValue, ZoneBehaviors } from "./types";
import { DisplayZoneValue, IOSettingsValue } from "./types";
import EntrydelayList from "./EntrydelayList";
import Partitions from "./Partitions";
import SmartalarmBoards from "./SmartalarmBoards";
import Remotecontrols from "./remotecontrol/Remotecontrols";
import RfidReaders from "./rfidreader/RfidReaders";
import SmartalarmUsers from "./SmartalarmUsers";

import { useDevice } from "../../../../utils/DeviceProvider";
import useEditedStyles from "../../../../hooks/useEditedStyles";
import { DeviceTemplate } from "../../../../utils/types";
import { uuidv4 } from "../../../../utils/utils";
import { portTypes } from "./SmartalarmBoard";

type SmartalarmState = {
  value: SmartalarmValue;
  saved: SmartalarmValue | null;
  setValue: (value: SmartalarmValue) => void;
};
type SmartalarmContextType = SmartalarmState & {};

const SmartalarmContext = createContext<SmartalarmContextType>({} as SmartalarmContextType);

export const SmartalarmProvider = ({ children }: PropsWithChildren<{}>) => {
  const {
    device: { template, currentValue, config, noConfig },
    setCurrentValue,
    setEmptyConfig,
  } = useDevice();

  const setValue = useCallback(
    (value: SmartalarmValue) => {
      setCurrentValue((old: any) => {
        if (isEqual(value, old.smartalarm)) {
          return old;
        }
        old.smartalarm = value;
        return { ...old };
      });
    },
    [setCurrentValue]
  );

  useEffect(() => {
    if (!template?.template || !Object.keys(template.template).includes("smartalarm")) {
      return;
    }
    if ((noConfig || (currentValue && !currentValue.smartalarm)) && template) {
      const config = emptyConfig(template.template);
      setEmptyConfig(config, "smartalarm");
      setCurrentValue((old: any) => ({ ...old, smartalarm: config }));
    }
  }, [template, currentValue, noConfig, setCurrentValue, setEmptyConfig]);

  if (!template?.template || !Object.keys(template.template).includes("smartalarm")) {
    return <>{children}</>;
  }

  let value;
  if (currentValue?.smartalarm) {
    value = defaultsDeep(currentValue?.smartalarm, emptyConfig(template.template)) as SmartalarmValue;
    delete value.panels[0].newItem;
  } else {
    value = currentValue?.smartalarm;
  }

  return (
    <SmartalarmContext.Provider
      value={{
        value: cloneDeep(value) as SmartalarmValue,
        saved: config?.toolboxConfig ? (config.toolboxConfig.smartalarm as SmartalarmValue) : null,
        setValue,
      }}
    >
      {children}
    </SmartalarmContext.Provider>
  );
};

export const useSmartalarm = () => {
  const smartalarmState = useContext(SmartalarmContext);
  if (smartalarmState === null) {
    throw new Error("useSmartalarm must be used within an SmartalarmProvider");
  }

  return smartalarmState;
};

export const calcZones = (value: IOSettingsValue[]): DisplayZoneValue[] => {
  const zones: DisplayZoneValue[] = [];
  if (!value) {
    return zones;
  }
  value.forEach((board, boardIndex) => {
    board?.ports?.forEach((port) => {
      port.value?.zoneValues?.forEach((zone, i) => {
        if (zone?.zone) {
          zones.push({
            uuid: zone.uuid,
            code: zone.zone,
            name: zone.name,
            output: port.value?.type === "OUT",
            disabled: !zone.enabled,
            deleted: port.value.deleted || i >= port.value.maxZones || board.deleted,
            entrydelay: !["OUT", "NULL"].includes(port.value?.type) && [ZoneBehaviors.DELAYED, ZoneBehaviors.FOLLOWER].includes(zone.behavior),
            behavior: zone.behavior,
            enabled: zone.enabled,
            port: port.nr,
            trigger: zone.trigger,
            board: boardIndex,
          });
        }
      });
    });
  });
  return zones;
};

export const useBeforeSave = () => {
  const { formatMessage } = useIntl();

  const beforeSave: BeforeSaveFunction<SmartalarmValue> = (value) => {
    // klónozás és objektum takarítás (akár teljesen az interface alapján végig)
    const {
      panels,
      plists: { partitions, zonesPlists },
      entrydelays,
      users,
      remotecontrol,
      rfids,
    } = value;
    const processed = {
      panels: panels.map(({ uuid, protocol, mac, ports, name, deleted }) => {
        const p = { uuid, protocol, mac, ports: cloneDeep(ports), name } as IOSettingsValue;
        if (deleted !== undefined) {
          p.deleted = deleted;
        }
        return p;
      }),
      plists: {
        partitions: partitions.map(({ uuid, name, code, home = false, followedPartitions, deleted }) => {
          const p = { uuid, name, code, home } as Partition;
          if (deleted !== undefined) {
            p.deleted = deleted;
          }
          if (followedPartitions && followedPartitions.length > 0) {
            p.followedPartitions = followedPartitions;
          }
          return p;
        }),
        zonesPlists: cloneDeep(zonesPlists),
      },
      entrydelays: cloneDeep(entrydelays),
      users: cloneDeep(users),
      remotecontrol: remotecontrol.map(({ name, uuid, buttons, physical, deleted }) => {
        const r = { name, uuid, buttons: cloneDeep(buttons), physical: cloneDeep(physical) } as RemotecontrolValue;
        if (deleted !== undefined) {
          r.deleted = deleted;
        }
        return r;
      }),
      rfids: cloneDeep(rfids),
    };
    // const processed = cloneDeep(value);
    if (!processed) {
      return { processed };
    }
    // deleted items
    const deletedZones = calcZones(processed.panels).filter((zone) => zone.deleted);
    const notEntrydelayZones = calcZones(processed.panels).filter((zone) => !zone.entrydelay);
    const deletedPartitions = processed.plists.partitions.filter((partition) => partition.deleted);

    // zones and partitions, entrydelays
    calcZones(processed.panels).forEach(({ uuid }) => {
      processed.plists.zonesPlists[uuid] = processed.plists.partitions
        .map((partition, index) => (partition.deleted ? null : processed.plists.zonesPlists[uuid]?.charAt(index)))
        .filter((item) => item !== null)
        .join("");
      // filter disabled zones
      Object.keys(processed.plists.zonesPlists).forEach((zoneUuid) => {
        if (processed.plists.zonesPlists[zoneUuid] === "") {
          delete processed.plists.zonesPlists[zoneUuid];
        }
      });
      // filter entry delays
      processed.entrydelays.forEach((entrydelay) => {
        entrydelay.zones = entrydelay.zones.filter((uuid) => ![...deletedZones, ...notEntrydelayZones].map(({ uuid }) => uuid).includes(uuid));
      });
    });
    // partitions and remotes
    processed.remotecontrol.forEach((remote) => {
      remote.buttons.forEach((button) => {
        if (button?.partition && deletedPartitions.map((partition) => partition.uuid).includes(button.partition)) {
          button.partition = "";
        }
      });
    });
    processed.rfids.forEach((reader) => {
      if (processed.panels.find((board) => board.uuid === reader.board && board.deleted)) {
        reader.board = "";
      }
      if (deletedPartitions.map((partition) => partition.uuid).includes(reader.partition)) {
        reader.partition = "";
      }
    });
    // users
    processed.users.forEach((user) => {
      if (!user.deleted) {
        return;
      }
      processed.rfids.forEach((reader) => {
        reader.cards.forEach((card) => {
          if (card.user === user.uuid) {
            card.user = "";
          }
        });
      });
      processed.remotecontrol.forEach((remote) => {
        remote.physical.forEach((physical) => {
          if (physical.user === user.uuid) {
            physical.user = "";
          }
        });
      });
    });

    // delete zones and partitions
    deletedZones.forEach(({ uuid }) => {
      delete processed.plists.zonesPlists[uuid];
    });
    // delete partitions
    processed.plists.partitions = processed.plists.partitions.filter((partition) => !partition.deleted);
    // delete panels
    processed.panels = processed.panels.filter((panel) => !panel.deleted);
    // delete ports
    processed.panels.forEach((panel, index) => (processed.panels[index].ports = panel.ports.filter((port) => !port.value.deleted)));
    // delete zones
    processed.panels.forEach((panel) =>
      panel.ports.forEach(
        (port, portIndex) => (panel.ports[portIndex].value.zoneValues = port.value.zoneValues.filter((zone, zoneIndex) => zoneIndex < port.value.maxZones))
      )
    );
    // delete entry delays
    processed.entrydelays = processed.entrydelays.filter(({ deleted }) => !deleted);
    // delete users
    processed.users = processed.users.filter((user) => !user.deleted);

    for (let i = processed.rfids.length - 1; i >= 0; i--) {
      if (processed.rfids[i].deleted) {
        processed.rfids.splice(i, 1);
      }
    }
    processed.rfids.forEach((reader) => {
      for (let i = reader.cards.length - 1; i >= 0; i--) {
        if (reader.cards[i].deleted) {
          reader.cards.splice(i, 1);
        }
      }
    });

    for (let i = processed.remotecontrol.length - 1; i >= 0; i--) {
      if (processed.remotecontrol[i].deleted) {
        processed.remotecontrol.splice(i, 1);
      }
    }
    processed.remotecontrol.forEach((remote) => {
      for (let i = remote.physical.length - 1; i >= 0; i--) {
        if (remote.physical[i].deleted) {
          remote.physical.splice(i, 1);
        }
      }
    });

    const errors: BeforeSaveErrorType[] = [];
    // port without resistor value
    const missingResistorValues = [] as string[];
    processed.panels.forEach(({ name, ports }, i) => {
      const portErrors = [] as string[];
      ports.forEach(({ nr, value: { type, resistor } }) => {
        if (!resistor) {
          const portType = portTypes.find(({ value }) => value === type);
          if (portType && portType.r) {
            portErrors.push(formatMessage({ defaultMessage: "IO port #{nr}" }, { nr }));
          }
        }
      });
      if (portErrors.length > 0) {
        missingResistorValues.push(
          `${
            name || (i === 0 ? formatMessage({ defaultMessage: "Master panel" }) : formatMessage({ defaultMessage: "Slave panel #{index}" }, { index: i }))
          }: ${portErrors.join(", ")}`
        );
      }
    });
    if (missingResistorValues.length > 0) {
      errors.push({ title: formatMessage({ defaultMessage: "Missing resistor value" }), details: missingResistorValues });
    }
    // empty partition
    processed.plists.partitions.forEach((partition, index) => {
      if (Object.values(processed.plists.zonesPlists).filter((value) => value.charAt(index) === "1").length === 0) {
        const title = formatMessage({ defaultMessage: "Partition without zone" });
        const emptyPartition = errors.find((error) => error.title === title);
        if (emptyPartition?.details) {
          emptyPartition.details.push(`${partition.name} (${partition.code})`);
        } else {
          errors.push({ title, details: [`${partition.name} (${partition.code})`] });
        }
      }
    });

    return { processed, errors: errors.length > 0 ? errors : undefined };
  };

  return beforeSave;
};

export const useSmartalarmSubPages = () => {
  const editedStyle = useEditedStyles();
  const { boardsEdited, partitionsEdited, entrydelaysEdited, usersEdited, remotesEdited, rfidEdited } = useEdited();

  return {
    sidebarOption: {
      link: "/smartalarm-boards",
      name: "SmartAlarm",
      icon: <SvgIcon component={SmartalarmIcon} />,
      classname: boardsEdited || partitionsEdited || entrydelaysEdited || usersEdited || remotesEdited || rfidEdited ? editedStyle.menuItem : "",
      subPages: [
        {
          link: "/smartalarm-boards",
          name: <FormattedMessage defaultMessage="Boards" />,
          icon: <SvgIcon component={BoardsIcon} />,
          classname: boardsEdited ? editedStyle.menuItem : "",
        },
        {
          link: "/smartalarm-partitions",
          name: <FormattedMessage defaultMessage="Partitions" />,
          icon: <SvgIcon component={PartitionsIcon} />,
          classname: partitionsEdited ? editedStyle.menuItem : "",
        },
        {
          link: "/smartalarm-entrydelay",
          name: <FormattedMessage defaultMessage="Entry delay" />,
          icon: <SvgIcon component={HourglassTopIcon} />,
          classname: entrydelaysEdited ? editedStyle.menuItem : "",
        },
        {
          link: "/smartalarm-users",
          name: <FormattedMessage defaultMessage="Users" />,
          icon: <PersonOutlineOutlinedIcon />,
          classname: usersEdited ? editedStyle.menuItem : "",
        },
        {
          link: "/smartalarm-remotes",
          name: <FormattedMessage defaultMessage="Remotecontrols" />,
          icon: <SettingsRemoteOutlinedIcon />,
          classname: remotesEdited ? editedStyle.menuItem : "",
        },
        {
          link: "/smartalarm-controls",
          name: <FormattedMessage defaultMessage="Control panels" />,
          icon: <SvgIcon component={RfidCardsIconIcon} />,
          classname: rfidEdited ? editedStyle.menuItem : "",
        },
      ],
    },
    children: [
      { link: "/smartalarm-boards", children: <SmartalarmBoards /> },
      { link: "/smartalarm-entrydelay", children: <EntrydelayList /> },
      { link: "/smartalarm-partitions", children: <Partitions /> },
      { link: "/smartalarm-users", children: <SmartalarmUsers /> },
      { link: "/smartalarm-remotes", children: <Remotecontrols /> },
      { link: "/smartalarm-controls", children: <RfidReaders /> },
    ],
  };
};

const useEdited = () => {
  const { value, saved } = useSmartalarm();
  const beforeSave = useBeforeSave();

  if (!value) {
    return {
      boardsEdited: false,
      partitionsEdited: false,
      entrydelaysEdited: false,
      usersEdited: false,
      remotesEdited: false,
      rfidEdited: false,
    };
  }

  const { processed } = beforeSave(value);

  return {
    boardsEdited: !isEqual(processed?.panels, saved?.panels),
    partitionsEdited: !isEqual(processed?.plists, saved?.plists),
    entrydelaysEdited: !isEqual(processed?.entrydelays, saved?.entrydelays),
    usersEdited: !isEqual(processed?.users, saved?.users),
    remotesEdited: !isEqual(processed.remotecontrol, saved?.remotecontrol),
    rfidEdited: !isEqual(processed.rfids, saved?.rfids),
  };
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const emptyConfig = (template: DeviceTemplate): SmartalarmValue => {
  return {
    panels: [
      {
        uuid: uuidv4(),
        protocol: null,
        mac: null,
        ports: [],
        // newItem: true,
      },
    ],
    plists: {
      partitions: [],
      zonesPlists: {},
    },
    entrydelays: [],
    users: [],
    remotecontrol: [],
    rfids: [],
  };
};

export const loadConfig = (old: SmartalarmValue, template: DeviceTemplate) => {
  const config = emptyConfig(template);
  old?.panels?.forEach((board, i) => {
    config.panels[i] = {} as IOSettingsValue;
    config.panels[i].uuid = board.uuid || uuidv4();
    config.panels[i].protocol = board.protocol;
    config.panels[i].mac = board.mac;
    config.panels[i].name = board.name;
    config.panels[i].ports = board.ports?.map((port) => {
      const { nr, value } = port;
      const { type, resistor, zoneValues, maxZones } = value || {};
      return {
        nr,
        value: {
          type,
          resistor,
          zoneValues: zoneValues.map((zone) => {
            const { uuid, enabled, name, time, delay, eventAlarm, eventRecovery, zone: z, behavior, entryDelay, trigger } = zone;
            return {
              uuid: uuid || uuidv4(),
              enabled,
              name,
              time,
              delay,
              eventAlarm,
              eventRecovery: eventRecovery || null,
              zone: z,
              behavior,
              entryDelay,
              trigger,
            };
          }),
          maxZones,
        },
      } as IOPortItem;
    });
  });
  old?.plists?.partitions?.forEach((partition, i) => {
    const { uuid, code, name, home } = partition;
    config.plists.partitions[i] = { uuid: uuid || uuidv4(), code, name, home };
  });
  config.entrydelays = old?.entrydelays ? cloneDeep(old.entrydelays) : [];
  const zones = calcZones(config.panels);
  zones.forEach(({ uuid }) => {
    config.plists.zonesPlists[uuid] =
      old?.plists.zonesPlists?.[uuid] ||
      Array.from({ length: config.plists.partitions.length })
        .map((_) => "0")
        .join("");
  });
  old?.users?.forEach((user, i) => {
    const { uuid, code, name } = user;
    config.users[i] = { uuid: uuid || uuidv4(), code, name };
  });
  old?.remotecontrol?.forEach((remote, i) => {
    const { uuid, name, buttons, physical } = remote;
    config.remotecontrol[i] = {
      uuid: uuid || uuidv4(),
      name,
      buttons: buttons.map((b) => {
        const { function: func, index, state, time, partition, press, event, zone } = b || {};
        return { function: func || "NULL", index, state, time, partition, press, event, zone };
      }),
      physical: physical.map((p) => {
        const { uuid, user, mac } = p;
        return { uuid: uuid || uuidv4(), user, mac };
      }),
    };
  });
  old?.rfids?.forEach((rfid, i) => {
    const { uuid, salt, name, board, protocol, mac, port, ports, partition, exitDelay, cards } = rfid;
    config.rfids[i] = {
      uuid: uuid || uuidv4(),
      salt,
      name,
      board,
      protocol: protocol || null,
      mac,
      port,
      ports,
      partition,
      exitDelay,
      cards,
    };
  });
  return config;
};
